import { ApiError, handleApiError, parseErrorResponse } from './errors';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
const MAX_RETRIES = 3;

let networkStatusCallback: ((status: 'connected' | 'disconnected' | 'reconnecting') => void) | null = null;

export function setNetworkStatusCallback(callback: (status: 'connected' | 'disconnected' | 'reconnecting') => void) {
  networkStatusCallback = callback;
}

function notifyNetworkStatus(status: 'connected' | 'disconnected' | 'reconnecting') {
  if (networkStatusCallback) {
    networkStatusCallback(status);
  }
}

class ApiClient {
  private token: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  setRefreshToken(refreshToken: string | null) {
    this.refreshToken = refreshToken;
  }

  getToken(): string | null {
    return this.token;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  private async checkHealth(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(API_BASE + '/health', {
        method: 'GET',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await fetch(API_BASE + '/auth/refresh/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      this.token = data.access_token;
      this.refreshToken = data.refresh_token;
      
      localStorage.setItem('lodiss_auth_token', JSON.stringify(this.token));
      localStorage.setItem('lodiss_refresh_token', JSON.stringify(this.refreshToken));
      
      return true;
    } catch {
      return false;
    }
  }

  private async doRefresh(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.refreshAccessToken();
    const result = await this.refreshPromise;
    this.isRefreshing = false;
    this.refreshPromise = null;
    return result;
  }

  private handleAuthFailure() {
    this.token = null;
    this.refreshToken = null;
    localStorage.removeItem('lodiss_auth_token');
    localStorage.removeItem('lodiss_refresh_token');
    
    const event = new CustomEvent('auth:logout');
    window.dispatchEvent(event);
  }

  private handleForbidden() {
    sessionStorage.setItem('session_expired_message', 'true');
    window.location.href = '/?page=login';
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = 'Bearer ' + this.token;
    }

    let response: Response;
    
    try {
      response = await fetch(API_BASE + endpoint, {
        ...options,
        headers,
      });
    } catch (networkError) {
      notifyNetworkStatus('reconnecting');
      
      if (retryCount < MAX_RETRIES) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.request(endpoint, options, retryCount + 1);
      }
      
      const isHealthOk = await this.checkHealth();
      if (isHealthOk) {
        notifyNetworkStatus('disconnected');
        throw {
          message: 'The service is temporarily unavailable',
          status: 503,
          isNetworkError: true,
        } as ApiError;
      }
      
      notifyNetworkStatus('disconnected');
      throw {
        message: 'Connection lost. Please check your internet connection.',
        status: 0,
        isNetworkError: true,
      } as ApiError;
    }

    notifyNetworkStatus('connected');

    if (response.status === 403) {
      this.handleForbidden();
      throw { message: 'Forbidden', status: 403 } as ApiError;
    }

    if (response.status === 401) {
      const refreshed = await this.doRefresh();
      
      if (refreshed) {
        headers['Authorization'] = 'Bearer ' + this.token;
        
        const retryResponse = await fetch(API_BASE + endpoint, {
          ...options,
          headers,
        });

        if (retryResponse.status === 403) {
          this.handleForbidden();
          throw { message: 'Forbidden', status: 403 } as ApiError;
        }

        if (retryResponse.status === 401) {
          this.handleAuthFailure();
          throw handleApiError(retryResponse);
        }

        if (!retryResponse.ok) {
          let errorData: unknown;
          try {
            errorData = await retryResponse.json();
          } catch {
            errorData = {};
          }
          throw parseErrorResponse(retryResponse, errorData);
        }

        if (retryResponse.status === 204) {
          return {} as T;
        }

        return retryResponse.json();
      } else {
        this.handleAuthFailure();
        throw handleApiError(response);
      }
    }

    if (!response.ok) {
      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        errorData = {};
      }
      throw parseErrorResponse(response, errorData);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += '?' + queryString;
      }
    }
    return this.request<T>(url, { method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async uploadFiles<T>(endpoint: string, files: File[], retryCount = 0): Promise<T> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = 'Bearer ' + this.token;
    }

    let response: Response;
    
    try {
      response = await fetch(API_BASE + endpoint, {
        method: 'POST',
        headers,
        body: formData,
      });
    } catch (networkError) {
      notifyNetworkStatus('reconnecting');
      
      if (retryCount < MAX_RETRIES) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.uploadFiles(endpoint, files, retryCount + 1);
      }
      
      notifyNetworkStatus('disconnected');
      throw {
        message: 'Connection lost. Please check your internet connection.',
        status: 0,
        isNetworkError: true,
      } as ApiError;
    }

    notifyNetworkStatus('connected');

    if (response.status === 403) {
      this.handleForbidden();
      throw { message: 'Forbidden', status: 403 } as ApiError;
    }

    if (response.status === 401) {
      const refreshed = await this.doRefresh();
      
      if (refreshed) {
        headers['Authorization'] = 'Bearer ' + this.token;
        
        const retryResponse = await fetch(API_BASE + endpoint, {
          method: 'POST',
          headers,
          body: formData,
        });

        if (retryResponse.status === 403) {
          this.handleForbidden();
          throw { message: 'Forbidden', status: 403 } as ApiError;
        }

        if (!retryResponse.ok) {
          let errorData: unknown;
          try {
            errorData = await retryResponse.json();
          } catch {
            errorData = {};
          }
          throw parseErrorResponse(retryResponse, errorData);
        }

        return retryResponse.json();
      } else {
        this.handleAuthFailure();
        throw { message: 'Session expired', status: 401 } as ApiError;
      }
    }

    if (!response.ok) {
      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        errorData = {};
      }
      throw parseErrorResponse(response, errorData);
    }

    return response.json();
  }
}

export const api = new ApiClient();
export type { ApiError };
